import React, { useContext, useEffect, useState } from 'react';
import {
  CognitiveSearchRequestModel,
  CognitiveSearchResultModel,
  ModuleBaseModel,
  ModuleBasePropsModel,
  ReportSearchModel,
} from '@/types';
import { ModuleBase, RecommendationsContext, UserContext } from '@/components';
import SearchFilters, { ReportsSearchFilterType } from '@/components/search/SearchFilters';
import BannerTemplate, { BannerTemplateModel } from './BannerTemplate';
import ReportSearchResult from '@/components/search/ReportSearchResult';
import { useRouter } from 'next/router';
import { getSearchFilters } from '@/utils';
import { searchReports } from '@/api';

type ReportsSearchModuleModel = ModuleBaseModel<
  {
    availableFilters: ReportsSearchFilterType[];
    cardType: 'ReportCardModel';
    requestFilter: {
      facetsMode: 'IncludeFacets';
      orderBy: 'score' | 'date';
      page: number;
      pageSize: number;
      publicSiteSearch: boolean;
      searchTerm: string;
    };
    topBannerImage?: BannerTemplateModel[];
    bottomBannerImage?: BannerTemplateModel[];
  } & ModuleBasePropsModel
>;

const ReportsSearchModule = (data: ReportsSearchModuleModel) => {
  const {
    props: { availableFilters, requestFilter, topBannerImage, bottomBannerImage },
  } = data;
  const router = useRouter();
  const recommendations = useContext(RecommendationsContext);

  const getSkeletons = (numberOfSkeletons: number) => {
    const skeletons: ReportSearchModel[] = [];
    for (let index = 0; index < numberOfSkeletons; index++) {
      skeletons.push({ reportId: index, isLoading: true } as ReportSearchModel);
    }
    return skeletons;
  };
  const pageSize = 12;
  const [result, setResult] = useState<CognitiveSearchResultModel<ReportSearchModel>>({
    facets: [],
    results: getSkeletons(pageSize),
    totalResults: 0,
  });
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState<'score' | 'date'>('score');

  const user = useContext(UserContext);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storage = localStorage.getItem('reportSearchOrderBy');
      if (storage) {
        setOrderBy(storage === 'score' ? 'score' : 'date');
      }
    }
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  const _setOrderBy = (value: 'score' | 'date') => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('reportSearchOrderBy', value);
    }
    setOrderBy(value);
    setTimeout(() => {
      getData();
    }, 100);
  };

  const getData = () => {
    const nameMap: { [key: string]: string } = {};

    availableFilters.forEach((filter) => {
      nameMap[filter.key] = filter.name;
    });

    const filters = getSearchFilters(router, nameMap);
    setLoading(true);

    setResult({ ...result, results: getSkeletons(pageSize) });

    const filterRequest: CognitiveSearchRequestModel = {
      ...requestFilter,
      searchTerm: Array.isArray(router.query.searchTerm) ? router.query.searchTerm[0] : router.query.searchTerm || '*',
      filters,
      pageSize,
      page: router.query.page ? Number(router.query.page) - 1 : 0,
      orderBy: localStorage.getItem('reportSearchOrderBy') === 'date' ? 'date' : 'score',
      account: user?.account,
      userId: user?.userId,
    };

    searchReports(filterRequest)
      .then((dataResponse) => {
        setResult(dataResponse);
        pushToRecentSearches(dataResponse);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const pushToRecentSearches = (dataResponse: CognitiveSearchResultModel<ReportSearchModel>) => {
    const searchTerm = Array.isArray(router.query.searchTerm) ? router.query.searchTerm[0] : router.query.searchTerm;

    if (typeof window !== 'undefined' && dataResponse.results.length > 0 && searchTerm && searchTerm.trim() !== '') {
      recommendations.pushToRecentSearches(searchTerm);
    }
  };

  return (
    <ModuleBase data={data} noPadding>
      {topBannerImage && <BannerTemplate {...topBannerImage[0]} />}
      <div id="listing-grid" />
      <SearchFilters filters={availableFilters} publicSiteSearch={requestFilter.publicSiteSearch} result={result} />
      <ReportSearchResult
        availableFilters={availableFilters}
        publicSiteSearch={requestFilter.publicSiteSearch}
        result={result}
        pageSize={pageSize}
        loading={loading}
        orderBy={orderBy}
        setOrderBy={_setOrderBy}
      />
      {bottomBannerImage && <BannerTemplate {...bottomBannerImage[0]} />}
    </ModuleBase>
  );
};

export default ReportsSearchModule;
